import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocale } from '../../context';
import { enUpper } from '../../utils/latinCase';
import { optimizeGameIconUrl, markGameIconFailed } from '../../data/casinoGames';
import { cdnFallbackUrl, withAssetCacheVersion } from '../../utils/assetCache';

function liveWinsIconSrc(image) {
  return optimizeGameIconUrl(image, 'sm') || image;
}

function liveWinsFullIconSrc(image) {
  if (!image || typeof image !== 'string') return null;
  try {
    const path = image.startsWith('http') ? new URL(image).pathname : image;
    const m = path.match(/\/uploads\/game-icon-thumbs\/t\d+\/([^/?#]+)$/i)
      || path.match(/\/uploads\/game-icons\/([^/?#]+)$/i);
    if (m) {
      return withAssetCacheVersion(cdnFallbackUrl(`/uploads/game-icons/${m[1]}`) || `/uploads/game-icons/${m[1]}`);
    }
  } catch {}
  return image.startsWith('http') ? image : withAssetCacheVersion(cdnFallbackUrl(image) || image);
}
import {
  ensureRealtimeConnected,
  isRealtimeConnected,
  subscribeRealtimeStatus,
  subscribeRealtimeTopic,
} from '../../utils/realtimeSocket';

const CARD_STEP = 202;
const POLL_MS = 4500;
const POLL_WS_MS = 60000;
const SETTLE_MS = 1400;
const ENTER_GAP_MS = 2200;

function formatAmount(value, locale) {
  return Number(value || 0).toLocaleString(locale === 'tr' ? 'tr-TR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function WinsIcon() {
  return (
    <svg
      className="live-wins__icon"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <g fill="currentColor" clipPath="url(#lw-wins-clip)">
        <path
          fillRule="evenodd"
          d="m16.305 7.078 2.745 3.37a9.15 9.15 0 0 1 2.09 5.818C21.14 20.964 17.554 24 12 24s-9.14-3.036-9.14-7.734c0-2.12.742-4.232 2.09-5.865l2.744-3.323zM12 9.938a.703.703 0 0 0-.703.703v1.037a2.105 2.105 0 0 0-1.414 1.981c0 1.163.946 2.11 2.11 2.11a.704.704 0 1 1-.704.703.703.703 0 1 0-1.406 0c0 .918.593 1.693 1.414 1.982v.624a.703.703 0 1 0 1.406 0v-.629a2.11 2.11 0 0 0 1.399-1.977c0-1.163-.947-2.11-2.11-2.11a.704.704 0 1 1 .689-.844l.029.283a.702.702 0 0 0 1.392-.142c0-.912-.586-1.684-1.399-1.977V10.64A.703.703 0 0 0 12 9.938"
          clipRule="evenodd"
        />
        <path d="M20.438 4.266c.388 0 .703.314.703.703v.703h.703a.703.703 0 0 1 0 1.406h-.703v.703a.703.703 0 1 1-1.407 0v-.703h-.703a.703.703 0 1 1 0-1.406h.703v-.703c0-.389.315-.703.703-.703M2.953 7.43a.703.703 0 1 1 1.218.704.703.703 0 0 1-1.218-.704M15.904.074a.703.703 0 0 1 .944.944l-.149.297c-.669 1.338-1.041 2.817-1.135 4.31H8.437C8.342 4.131 7.97 2.653 7.3 1.315l-.149-.297a.703.703 0 0 1 .944-.944l2.36 1.18L11.502.206a.703.703 0 0 1 .994 0l1.048 1.048zM3.563 0c.388 0 .702.315.703.703v.703h.703a.703.703 0 1 1 0 1.407h-.703v.703a.703.703 0 1 1-1.407 0v-.703h-.703a.703.703 0 1 1 0-1.407h.703V.703c0-.388.315-.703.704-.703" />
      </g>
      <defs>
        <clipPath id="lw-wins-clip">
          <path fill="#fff" d="M0 0h24v24H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

function FlameIcon({ id = 'lw-flame' }) {
  const gradId = `${id}-grad`;
  const clipId = `${id}-clip`;
  return (
    <svg className="live-wins__heated-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 34 34" aria-hidden="true">
      <g fillRule="evenodd" clipPath={`url(#${clipId})`} clipRule="evenodd">
        <path
          fill={`url(#${gradId})`}
          d="M29.453 21.975c-.28 6.674-5.777 12-12.52 12-6.922 0-12.534-5.8-12.534-12.533 0-.9-.016-2.7 1.334-5.734.807-1.816 1.314-2.957 1.6-4 .157-.573.462-1.484 1.333 0 .514.875.533 2.134.533 2.134s1.91-1.466 3.2-4.267c1.891-4.106.383-6.56-.133-8.267-.178-.59-.29-1.651.933-1.2 1.247.46 4.544 2.768 6.267 5.2 2.46 3.472 3.333 6.8 3.333 6.8s.788-.977 1.067-2c.315-1.155.32-2.298 1.333-1.066.964 1.171 2.395 3.373 3.2 5.466 1.463 3.802 1.054 7.467 1.054 7.467"
        />
        <path
          fill="#fc9502"
          d="M16.932 33.975a8.667 8.667 0 0 1-8.666-8.667c0-2.888 1.163-4.641 3.586-7.02 1.55-1.522 3.002-3.392 3.62-4.665.12-.25.397-1.557 1.463-.028.559.803 1.435 2.23 1.997 3.447a15.1 15.1 0 0 1 1.2 4.133s.95-.56 1.6-2c.21-.464.634-2.222 1.82-.465.87 1.29 2.064 3.609 2.047 6.598a8.667 8.667 0 0 1-8.667 8.667"
        />
        <path
          fill="#fce202"
          d="M17.066 24.508c1.234 0 1.234 2.284 2.8 5.334 1.044 2.03-.517 4.133-2.8 4.133s-3.466-1.85-3.466-4.133 2.233-5.334 3.466-5.334"
        />
      </g>
      <defs>
        <linearGradient id={gradId} x1="16.952" x2="16.952" y1="34" y2="0.025" gradientUnits="userSpaceOnUse">
          <stop stopColor="#ff4c0d" />
          <stop offset="1" stopColor="#fc9502" />
        </linearGradient>
        <clipPath id={clipId}>
          <path fill="#fff" d="M0 0h34v34H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="15" fill="none" viewBox="0 0 13 15" aria-hidden="true">
      <path fill="currentColor" d="M12 5.467c1.333.77 1.333 2.694 0 3.464l-9 5.196c-1.333.77-3-.192-3-1.732V2.003C0 .463 1.667-.499 3 .271z" />
    </svg>
  );
}

export default function LiveWinsRail({ onNavigate }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const [visible, setVisible] = useState([]);
  const [leadId, setLeadId] = useState(null);
  const trackRef = useRef(null);
  const enterGenRef = useRef(0);
  const seededRef = useRef(false);
  const knownIdsRef = useRef(new Set());
  const queueRef = useRef([]);
  const busyRef = useRef(false);
  const drainTimerRef = useRef(0);

  const runEnter = (item) => {
    enterGenRef.current += 1;
    const gen = enterGenRef.current;
    setVisible((prev) => [item, ...prev.filter((x) => x.id !== item.id)].slice(0, 12));
    setLeadId(`${item.id}-${gen}`);
  };

  const drainQueue = () => {
    if (busyRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    busyRef.current = true;
    runEnter(next);
    window.clearTimeout(drainTimerRef.current);
    drainTimerRef.current = window.setTimeout(() => {
      busyRef.current = false;
      drainQueue();
    }, SETTLE_MS + ENTER_GAP_MS);
  };

  useEffect(() => {
    let alive = true;
    let pollTimer = 0;

    const ingest = (items) => {
      if (!Array.isArray(items) || !items.length) return;

      if (!seededRef.current) {
        seededRef.current = true;
        knownIdsRef.current = new Set(items.map((x) => x.id));
        setVisible(items.slice(0, 12));
        return;
      }

      const fresh = [];
      for (const item of items) {
        if (knownIdsRef.current.has(item.id)) break;
        fresh.push(item);
      }
      if (!fresh.length) return;

      for (const item of fresh) knownIdsRef.current.add(item.id);
      if (knownIdsRef.current.size > 240) {
        knownIdsRef.current = new Set(items.map((x) => x.id));
      }

      queueRef.current.push(...fresh);
      drainQueue();
    };

    const load = async () => {
      try {
        const res = await fetch('/api/casino/live-wins');
        if (!res.ok) return;
        const json = await res.json();
        if (!alive) return;
        if (json?.enabled === false) {
          setVisible([]);
          return;
        }
        ingest(json.items);
      } catch {
      }
    };

    const armPoll = () => {
      window.clearInterval(pollTimer);
      const ms = isRealtimeConnected() ? POLL_WS_MS : POLL_MS;
      pollTimer = window.setInterval(() => {
        if (!isRealtimeConnected()) load();
      }, ms);
    };

    ensureRealtimeConnected();
    const unsub = subscribeRealtimeTopic('liveWins', (data) => {
      if (!alive) return;
      if (data?.enabled === false) {
        setVisible([]);
        return;
      }
      ingest(data?.items);
    });
    const unsubStatus = subscribeRealtimeStatus(() => {
      armPoll();
      if (!isRealtimeConnected() && alive) load();
    });
    load();
    armPoll();

    return () => {
      alive = false;
      unsub();
      unsubStatus();
      window.clearInterval(pollTimer);
      window.clearTimeout(drainTimerRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const resetTrack = () => {
      track.style.transition = 'none';
      track.style.transform = 'translate3d(0, 0, 0)';
    };

    const onResize = () => {
      enterGenRef.current += 1;
      setLeadId(null);
      resetTrack();
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useLayoutEffect(() => {
    if (!leadId) return undefined;
    const track = trackRef.current;
    if (!track) return undefined;
    const firstSlide = track.querySelector('.live-wins__slide');
    const step = firstSlide ? Math.round(firstSlide.getBoundingClientRect().width) || CARD_STEP : CARD_STEP;
    const gen = enterGenRef.current;
    const ease = 'cubic-bezier(0.16, 1, 0.3, 1)';
    track.style.transition = 'none';
    track.style.transform = `translate3d(-${step}px, 0, 0)`;
    void track.offsetHeight;
    track.style.transition = `transform ${SETTLE_MS}ms ${ease}`;
    track.style.transform = 'translate3d(0, 0, 0)';
    const done = window.setTimeout(() => {
      if (gen !== enterGenRef.current) return;
      track.style.transition = 'none';
      track.style.transform = 'translate3d(0, 0, 0)';
    }, SETTLE_MS + 40);
    return () => {
      window.clearTimeout(done);
      track.style.transition = 'none';
      track.style.transform = 'translate3d(0, 0, 0)';
    };
  }, [leadId]);

  if (!visible.length) return null;

  const openGame = (item) => {
    if (!item?.gameId) {
      onNavigate?.('casino', { casinoTabId: '-1' });
      return;
    }
    onNavigate?.('casino', {
      casinoTabId: '-1',
      casinoGameId: String(item.gameId),
      casinoGameDemo: false,
    });
  };

  return (
    <section
      className="live-wins"
      role="region"
      aria-roledescription="carousel"
      aria-label={tr('home.liveWins.title')}
      data-lite="false"
    >
      <div className="live-wins__header" data-breakpoint="md">
        <div className="live-wins__header-left">
          <WinsIcon />
          <h2 className="live-wins__title">{tr('home.liveWins.title')}</h2>
        </div>
      </div>
      <div className="live-wins__viewport">
        <div ref={trackRef} className="live-wins__track">
          {visible.map((item, index) => {
            const isLeading = index === 0 && leadId?.startsWith(`${item.id}-`);
            const lines = item.titleLines?.length ? item.titleLines : [item.name];
            return (
              <div
                key={`${item.id}-${index === 0 ? leadId || '0' : index}`}
                className="live-wins__slide"
                role="group"
                aria-roledescription="slide"
              >
                <div
                  role="listitem"
                  data-lite="false"
                  data-is-massive={item.massive ? 'true' : 'false'}
                  className={[
                    'live-wins__card',
                    item.massive ? 'is-massive' : '',
                    item.heated ? 'is-heated' : '',
                    isLeading ? 'is-leading' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => openGame(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openGame(item);
                    }
                  }}
                  tabIndex={0}
                >
                  <div className="live-wins__cover">
                    {item.image ? (
                      <img
                        src={liveWinsIconSrc(item.image)}
                        alt={item.name}
                        loading="lazy"
                        decoding="async"
                        width={66}
                        height={88}
                        onError={(e) => {
                          const el = e.currentTarget;
                          const cur = el.getAttribute('src') || '';
                          markGameIconFailed(cur);
                          if (el.dataset.vbFb === '1') {
                            el.style.visibility = 'hidden';
                            return;
                          }
                          const full = liveWinsFullIconSrc(item.image);
                          if (full && full !== cur) {
                            el.dataset.vbFb = '1';
                            el.src = full;
                            return;
                          }
                          el.style.visibility = 'hidden';
                        }}
                      />
                    ) : (
                      <div className="live-wins__cover-fallback" />
                    )}
                    <div className="live-wins__caption" aria-hidden="true" lang="en">
                      <div className="live-wins__caption-content">
                        <div className="live-wins__caption-title">
                          {lines.map((line) => (
                            <span
                              key={line}
                              className="live-wins__caption-line"
                              style={{ '--line-len': Math.max(String(line).length, 4) }}
                            >
                              {enUpper(line)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="live-wins__play"
                      aria-label={item.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        openGame(item);
                      }}
                    >
                      <PlayIcon />
                    </button>
                  </div>
                  <div className="live-wins__meta">
                    <div className="live-wins__price">
                      {formatAmount(item.amount, locale)} {item.currencySymbol || 'TL'}
                    </div>
                    {item.heated ? (
                      <div className="live-wins__heated">
                        <FlameIcon id={`flame-${item.id}-${index}`} />
                        <span className="live-wins__heated-name">{tr('home.liveWins.heated')}</span>
                      </div>
                    ) : null}
                    <div className="live-wins__city">{item.city}</div>
                    <div className="live-wins__user">{item.userMask}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
